import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { ChevronDown, Send, Landmark, Link2, Copy, ExternalLink, Shield, TrendingUp, DollarSign, Clock, Percent, Eye } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL || (typeof window !== "undefined" ? window.location.origin : "")}/api`;
const FRONTEND = process.env.REACT_APP_BACKEND_URL || (typeof window !== "undefined" ? window.location.origin : "");
const statusFlow = ["pending_payment", "payment_received", "processing", "shipped", "in_transit", "delivered", "completed", "disputed"];
const SL = { pending_payment: "Pending Execution", payment_received: "Settlement Secured", processing: "Execution in Progress", shipped: "Shipment Dispatched", in_transit: "In Transit", delivered: "Delivered — Settlement Triggered", completed: "Settlement Released", disputed: "Settlement Held" };
const SC = { pending_payment: "text-yellow-400 bg-yellow-400/10 border-yellow-400/30", payment_received: "text-blue-400 bg-blue-400/10 border-blue-400/30", processing: "text-purple-400 bg-purple-400/10 border-purple-400/30", shipped: "text-cyan-400 bg-cyan-400/10 border-cyan-400/30", in_transit: "text-orange-400 bg-orange-400/10 border-orange-400/30", delivered: "text-[#22C55E] bg-[#22C55E]/10 border-[#22C55E]/30", completed: "text-[#22C55E] bg-[#22C55E]/10 border-[#22C55E]/30", disputed: "text-red-400 bg-red-400/10 border-red-400/30" };
function fmtAmt(a, c) { return new Intl.NumberFormat("en-US", { style: "currency", currency: c || "USD", minimumFractionDigits: 0 }).format(a); }
function fmtShort(v) { return v >= 1000000 ? `$${(v / 1000000).toFixed(2)}M` : v >= 1000 ? `$${(v / 1000).toFixed(1)}K` : `$${Math.round(v)}`; }

export default function AdminTransactions() {
  const { user } = useAuth();
  const isDemo = user?.is_demo === true;
  const [txns, setTxns] = useState([]);
  const [econ, setEcon] = useState(null);
  const [filter, setFilter] = useState("all");
  const [linkModal, setLinkModal] = useState(null);
  const [copied, setCopied] = useState("");
  const fetchTxns = useCallback(async () => {
    try {
      const [t, e] = await Promise.all([
        axios.get(`${API}/admin/transactions`, { withCredentials: true }),
        axios.get(`${API}/admin/platform-economics`, { withCredentials: true }),
      ]);
      setTxns(t.data); setEcon(e.data);
    } catch (e) { console.error(e); }
  }, []);
  useEffect(() => { fetchTxns(); }, [fetchTxns]);
  const updateStatus = async (id, s) => { await axios.put(`${API}/admin/transactions/${id}/status`, { status: s }, { withCredentials: true }); fetchTxns(); };
  const sendLink = async (id) => { const { data } = await axios.post(`${API}/admin/transactions/${id}/send-link`, {}, { withCredentials: true }); setLinkModal({ buyerLink: `${FRONTEND}/pay/${data.buyer_token}`, supplierLink: `${FRONTEND}/track/${data.supplier_token}` }); fetchTxns(); };
  const copyLink = (l, lb) => { navigator.clipboard.writeText(l); setCopied(lb); setTimeout(() => setCopied(""), 2000); };
  const filtered = filter === "all" ? txns : txns.filter(t => t.status === filter);

  return (
    <div data-testid="admin-transactions">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="font-['Outfit'] text-2xl font-bold text-white">Flow Engine</h1>
          <p className="text-[#A1B2A8]/60 text-sm">Capital deployed · Delta GreenX revenue · Buyer savings · Returns lifecycle</p>
        </div>
        <div className="flex items-center gap-3">
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="w-[180px] bg-[#09120D] border-[#1A2E22] text-white text-sm h-9" data-testid="txn-filter">
              <SelectValue placeholder="Filter" />
            </SelectTrigger>
            <SelectContent className="bg-[#09120D] border-[#1A2E22] text-white">
              <SelectItem value="all">All Status</SelectItem>
              {statusFlow.map(s => <SelectItem key={s} value={s}>{SL[s]}</SelectItem>)}
            </SelectContent>
          </Select>
          <span className="text-[#A1B2A8]/50 text-xs">{filtered.length} flows</span>
        </div>
      </div>

      {/* Platform Revenue / Returns Lifecycle KPIs */}
      {econ && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-6" data-testid="platform-economics">
          <div className="bg-[#09120D] border border-[#1A2E22] p-4">
            <div className="flex items-center gap-1.5 mb-1"><DollarSign className="w-3 h-3 text-white" /><span className="text-[#A1B2A8] text-[10px] uppercase tracking-wider">Capital Deployed</span></div>
            <p className="font-['Outfit'] text-xl font-bold text-white">{fmtShort(econ.total_capital_deployed)}</p>
          </div>
          <div className="bg-[#22C55E]/5 border border-[#22C55E]/30 p-4">
            <div className="flex items-center gap-1.5 mb-1"><TrendingUp className="w-3 h-3 text-[#22C55E]" /><span className="text-[#22C55E] text-[10px] uppercase tracking-wider">Revenue Realized</span></div>
            <p className="font-['Outfit'] text-xl font-bold text-[#22C55E]">{fmtShort(econ.total_revenue_realized)}</p>
            <p className="text-[#22C55E]/60 text-[10px] mt-0.5">{econ.completed_flows} completed</p>
          </div>
          <div className="bg-[#09120D] border border-[#1A2E22] p-4">
            <div className="flex items-center gap-1.5 mb-1"><TrendingUp className="w-3 h-3 text-yellow-400" /><span className="text-[#A1B2A8] text-[10px] uppercase tracking-wider">Revenue Pipeline</span></div>
            <p className="font-['Outfit'] text-xl font-bold text-yellow-400">{fmtShort(econ.total_revenue_pipeline)}</p>
            <p className="text-[#A1B2A8]/50 text-[10px] mt-0.5">All active flows</p>
          </div>
          <div className="bg-[#09120D] border border-[#1A2E22] p-4">
            <div className="flex items-center gap-1.5 mb-1"><DollarSign className="w-3 h-3 text-blue-400" /><span className="text-[#A1B2A8] text-[10px] uppercase tracking-wider">Buyer Savings</span></div>
            <p className="font-['Outfit'] text-xl font-bold text-blue-400">{fmtShort(econ.total_buyer_savings)}</p>
            <p className="text-[#A1B2A8]/50 text-[10px] mt-0.5">vs traditional</p>
          </div>
          <div className="bg-[#09120D] border border-[#1A2E22] p-4">
            <div className="flex items-center gap-1.5 mb-1"><Percent className="w-3 h-3 text-purple-400" /><span className="text-[#A1B2A8] text-[10px] uppercase tracking-wider">Avg Platform Fee</span></div>
            <p className="font-['Outfit'] text-xl font-bold text-purple-400">{econ.avg_fee_pct}%</p>
          </div>
          <div className="bg-[#09120D] border border-[#1A2E22] p-4">
            <div className="flex items-center gap-1.5 mb-1"><Clock className="w-3 h-3 text-cyan-400" /><span className="text-[#A1B2A8] text-[10px] uppercase tracking-wider">Avg Days to Close</span></div>
            <p className="font-['Outfit'] text-xl font-bold text-cyan-400">{econ.avg_days_to_close}d</p>
          </div>
        </div>
      )}

      <div className="space-y-3">
        {filtered.map((txn) => (
          <div key={txn.id} className="bg-[#09120D] border border-[#1A2E22] p-6 hover:border-[#22C55E]/30 transition-colors" data-testid={`txn-${txn.id}`}>
            <div className="flex flex-col xl:flex-row xl:items-start justify-between gap-4">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-3 flex-wrap">
                  <Landmark className={`w-4 h-4 flex-shrink-0 ${SC[txn.status]?.split(" ")[0] || "text-white"}`} />
                  <span className={`text-xs px-2 py-0.5 border ${SC[txn.status] || ""}`}>{SL[txn.status] || txn.status}</span>
                  {txn.capital_structure && <span className="text-[#A1B2A8]/60 text-xs border border-[#1A2E22] px-2 py-0.5">{txn.capital_structure}</span>}
                  {txn.risk_score !== undefined && (
                    <span className={`text-xs px-2 py-0.5 border ${txn.risk_score >= 80 ? "text-[#22C55E] bg-[#22C55E]/10 border-[#22C55E]/30" : txn.risk_score >= 60 ? "text-yellow-400 bg-yellow-400/10 border-yellow-400/30" : "text-red-400 bg-red-400/10 border-red-400/30"}`}>
                      <Shield className="w-3 h-3 inline mr-1" />{txn.risk_score}
                    </span>
                  )}
                  {txn.payment_link_sent && <span className="text-[#22C55E]/60 text-xs flex items-center gap-1"><Link2 className="w-3 h-3" />Link Active</span>}
                </div>
                <p className="text-white font-['Outfit'] font-semibold text-sm mb-2">{txn.description}</p>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div><p className="text-[#A1B2A8] text-xs">Buyer</p><p className="text-white text-sm">{txn.buyer_name}</p></div>
                  <div><p className="text-[#A1B2A8] text-xs">Supplier</p><p className="text-white text-sm">{txn.supplier_name}</p></div>
                  <div><p className="text-[#A1B2A8] text-xs">Structure</p><p className="text-white text-sm">{txn.capital_structure || txn.payment_method}</p></div>
                </div>

                {/* Returns Lifecycle */}
                {txn.economics && (
                  <div className="mt-3 pt-3 border-t border-[#1A2E22] grid grid-cols-2 md:grid-cols-5 gap-3" data-testid={`txn-economics-${txn.id}`}>
                    <div>
                      <p className="text-[#A1B2A8] text-[10px] uppercase tracking-wider">Capital Deployed</p>
                      <p className="text-white font-['Outfit'] font-semibold text-sm">{fmtShort(txn.economics.capital_deployed)}</p>
                    </div>
                    <div>
                      <p className="text-[#22C55E]/70 text-[10px] uppercase tracking-wider">Delta GreenX Revenue</p>
                      <p className="text-[#22C55E] font-['Outfit'] font-semibold text-sm">
                        {fmtShort(txn.economics.delta_greenx_revenue)} {txn.economics.revenue_realized ? "" : <span className="text-[#22C55E]/50 text-[10px]">(projected)</span>}
                      </p>
                    </div>
                    <div>
                      <p className="text-[#A1B2A8] text-[10px] uppercase tracking-wider">Fee %</p>
                      <p className="text-purple-400 font-['Outfit'] font-semibold text-sm">{(txn.economics.platform_fee_pct * 100).toFixed(2)}%</p>
                    </div>
                    <div>
                      <p className="text-[#A1B2A8] text-[10px] uppercase tracking-wider">Buyer Savings</p>
                      <p className="text-blue-400 font-['Outfit'] font-semibold text-sm">{fmtShort(txn.economics.buyer_working_capital_savings)}</p>
                    </div>
                    <div>
                      <p className="text-[#A1B2A8] text-[10px] uppercase tracking-wider">Days Saved</p>
                      <p className="text-cyan-400 font-['Outfit'] font-semibold text-sm">{txn.economics.days_saved}d <span className="text-[#A1B2A8]/50 text-[10px]">of {txn.economics.traditional_days}</span></p>
                    </div>
                  </div>
                )}

                {txn.trigger_conditions && txn.trigger_conditions.length > 0 && (
                  <div className="mt-3 pt-3 border-t border-[#1A2E22] flex flex-wrap gap-2">
                    {txn.trigger_conditions.map((tc, i) => (
                      <span key={i} className={`text-[10px] px-2 py-0.5 border ${tc.status === "completed" ? "text-[#22C55E] border-[#22C55E]/30 bg-[#22C55E]/5" : "text-[#A1B2A8]/40 border-[#1A2E22]"}`}>
                        {tc.milestone}
                      </span>
                    ))}
                  </div>
                )}
              </div>
              <div className="flex items-start gap-3 flex-shrink-0">
                <div className="text-right">
                  <p className="text-[#A1B2A8] text-xs">Trade Value</p>
                  <p className="font-['Outfit'] text-xl font-bold text-[#22C55E]">{fmtAmt(txn.amount, txn.currency)}</p>
                </div>
                <div className="flex flex-col gap-1">
                  {isDemo ? (
                    <span className="flex items-center gap-1.5 text-[#22C55E] text-xs border border-[#22C55E]/30 bg-[#22C55E]/5 px-3 py-1.5" data-testid={`txn-readonly-${txn.id}`}>
                      <Eye className="w-3 h-3" /> Read-only
                    </span>
                  ) : (
                    <>
                      {!txn.payment_link_sent && (
                        <button onClick={() => sendLink(txn.id)} className="text-[#22C55E] text-xs border border-[#22C55E]/30 px-3 py-1.5 hover:bg-[#22C55E]/10 flex items-center gap-1" data-testid={`send-link-${txn.id}`}><Send className="w-3 h-3" />Send Link</button>
                      )}
                      {txn.payment_link_sent && (
                        <button onClick={() => setLinkModal({ buyerLink: `${FRONTEND}/pay/${txn.buyer_token}`, supplierLink: `${FRONTEND}/track/${txn.supplier_token}` })} className="text-[#A1B2A8] text-xs border border-[#1A2E22] px-3 py-1.5 hover:text-white flex items-center gap-1" data-testid={`view-links-${txn.id}`}><ExternalLink className="w-3 h-3" />Links</button>
                      )}
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <button className="text-[#A1B2A8] text-xs border border-[#1A2E22] px-3 py-1.5 flex items-center gap-1 hover:text-white" data-testid={`txn-action-${txn.id}`}>Status <ChevronDown className="w-3 h-3" /></button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent className="bg-[#09120D] border-[#1A2E22] text-white">
                          {statusFlow.map(s => <DropdownMenuItem key={s} onClick={() => updateStatus(txn.id, s)} className={`${SC[s]?.split(" ")[0] || "text-white"} focus:bg-white/5 cursor-pointer text-xs`}>{SL[s]}</DropdownMenuItem>)}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        ))}
        {filtered.length === 0 && <p className="text-center text-[#A1B2A8] text-sm py-12">No active flows</p>}
      </div>
      <Dialog open={!!linkModal} onOpenChange={() => setLinkModal(null)}>
        <DialogContent className="bg-[#09120D] border-[#1A2E22] text-white max-w-lg" data-testid="links-modal">
          <DialogHeader>
            <DialogTitle className="text-white font-['Outfit']">Execution Links</DialogTitle>
            <DialogDescription className="text-[#A1B2A8]">Share secure execution links with buyer and exporter.</DialogDescription>
          </DialogHeader>
          {linkModal && (
            <div className="space-y-4 mt-2">
              {[["Buyer Execution Link", linkModal.buyerLink, "buyer"], ["Exporter Tracking Link", linkModal.supplierLink, "supplier"]].map(([label, link, key]) => (
                <div key={key}>
                  <p className="text-[#A1B2A8] text-xs mb-1">{label}</p>
                  <div className="flex items-center gap-2 bg-[#050A07] border border-[#1A2E22] p-3">
                    <p className="text-[#22C55E] text-xs truncate flex-1 font-mono">{link}</p>
                    <button onClick={() => copyLink(link, key)} className="text-[#A1B2A8] hover:text-white flex-shrink-0" data-testid={`copy-${key}-link`}><Copy className="w-4 h-4" /></button>
                  </div>
                  {copied === key && <p className="text-[#22C55E] text-xs mt-1">Copied!</p>}
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
